"use strict";
/**
 * Additional Markets UI
 *
 * Manages the visual indicators for additional market assignments:
 * - Badge icons on order buttons (any layout) that have assignments
 * - Assignment records in the settings info tooltip with remove buttons
 *
 * @module handlers/modals/additional-markets-ui
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.CLEAR_ADDITIONAL_MARKETS_BTN_IDS = void 0;
exports.setEditAssignmentCallback = setEditAssignmentCallback;
exports.updateAdditionalMarketBadges = updateAdditionalMarketBadges;
exports.updateSettingsInfoAdditionalMarkets = updateSettingsInfoAdditionalMarkets;
const additional_markets_service_1 = require("../../services/additional-markets-service");
const order_button_registry_1 = require("../../services/order-button-registry");
let editAssignmentCallback = null;
/**
 * Register the function that should be called when the user requests to edit
 * an existing assignment from the settings-info tooltip.
 *
 * @param cb - Callback receiving the assignment ID to open in edit mode.
 */
function setEditAssignmentCallback(cb) {
    editAssignmentCallback = cb;
}
// ---------------------------------------------------------------------------
// Badge management
// ---------------------------------------------------------------------------
const BADGE_CLASS = "am-badge";
/**
 * DOM ids of the "clear all assignments" (🗑️) button, one per sport's
 * factor row (traditional mode has no factor row, so no button there).
 */
exports.CLEAR_ADDITIONAL_MARKETS_BTN_IDS = [
    "baseball-clear-am-btn",
    "basketball-clear-am-btn",
    "soccer-clear-am-btn",
];
/**
 * Add or remove the "+" badge on a single host element, idempotently.
 *
 * @param host - Element to badge (an order button, or the info button).
 * @param on - Whether the badge should be present.
 */
function setBadge(host, on) {
    const badge = host.querySelector(`.${BADGE_CLASS}`);
    if (on && !badge) {
        const created = document.createElement("span");
        created.className = BADGE_CLASS;
        created.textContent = "+";
        created.title = "Additional market(s) assigned";
        host.appendChild(created);
    }
    else if (!on && badge) {
        badge.remove();
    }
}
/**
 * Update badge indicators on all order buttons (every layout).
 * Adds a small "+" badge to buttons that have additional market assignments.
 * Removes badges from buttons without assignments.
 *
 * The settings-info button gets the same badge whenever ANY assignment
 * exists: it is the only way into the assignment list, and "ℹ️" on its own
 * says nothing about extras being wired up.
 */
function updateAdditionalMarketBadges() {
    const buttonsWithAssignments = (0, additional_markets_service_1.getButtonsWithAssignments)();
    // Iterate over all button IDs
    for (const [buttonId, domIds] of Object.entries(order_button_registry_1.ORDER_BUTTON_DOM_IDS)) {
        const hasAssignment = buttonsWithAssignments.has(buttonId);
        for (const domId of domIds) {
            const btn = document.getElementById(domId);
            if (!btn)
                continue;
            setBadge(btn, hasAssignment);
        }
    }
    const hasAny = buttonsWithAssignments.size > 0;
    const infoBtn = document.getElementById("settings-info-btn");
    if (infoBtn) {
        setBadge(infoBtn, hasAny);
        infoBtn.title = hasAny
            ? "View additional market assignments"
            : "View settings";
    }
    // Show/hide the "clear all" button in every sport's factor row
    for (const btnId of exports.CLEAR_ADDITIONAL_MARKETS_BTN_IDS) {
        document.getElementById(btnId)?.classList.toggle("hidden", !hasAny);
    }
}
// ---------------------------------------------------------------------------
// Settings info tooltip – additional markets section
// ---------------------------------------------------------------------------
const TOOLTIP_SECTION_ID = "am-tooltip-section";
const TOOLTIP_EMPTY_ID = "am-tooltip-empty";
/**
 * Update the settings info tooltip to include records for each
 * additional market assignment, with X buttons to remove them.
 *
 * The popup holds nothing else, so with no assignments it renders an
 * explicit empty state — otherwise it opens as a blank floating card.
 */
function updateSettingsInfoAdditionalMarkets() {
    const popup = document.getElementById("settings-info-popup");
    if (!popup)
        return;
    // Remove whatever was rendered last time (section or empty state).
    document.getElementById(TOOLTIP_SECTION_ID)?.remove();
    document.getElementById(TOOLTIP_EMPTY_ID)?.remove();
    const assignments = (0, additional_markets_service_1.getAdditionalAssignments)();
    if (assignments.length === 0) {
        const empty = document.createElement("p");
        empty.id = TOOLTIP_EMPTY_ID;
        empty.className = "am-tooltip-empty";
        empty.textContent = "No additional markets assigned.";
        popup.appendChild(empty);
        return;
    }
    const section = document.createElement("div");
    section.id = TOOLTIP_SECTION_ID;
    section.className = "am-tooltip-section";
    const header = document.createElement("p");
    header.innerHTML = "<strong>Additional Markets:</strong>";
    section.appendChild(header);
    for (const assignment of assignments) {
        const row = document.createElement("div");
        row.className = "am-tooltip-row";
        const info = document.createElement("div");
        info.className = "am-tooltip-info";
        info.title = `Ticker: ${assignment.marketTicker} — Click to edit`;
        // Ticker line: de-emphasise the shared prefix, bold the trailing
        // segment that actually distinguishes one market from the next.
        const ticker = document.createElement("div");
        ticker.className = "am-ticker";
        const splitAt = assignment.marketTicker.lastIndexOf("-");
        if (splitAt > 0 && splitAt < assignment.marketTicker.length - 1) {
            const prefix = document.createElement("span");
            prefix.className = "am-ticker-prefix";
            prefix.textContent = assignment.marketTicker.slice(0, splitAt);
            const suffix = document.createElement("span");
            suffix.className = "am-ticker-suffix";
            suffix.textContent = assignment.marketTicker.slice(splitAt);
            ticker.append(prefix, suffix);
        }
        else {
            ticker.textContent = assignment.marketTicker;
        }
        // Meta chips: side / dollar amount / manual price.
        const meta = document.createElement("div");
        meta.className = "am-meta";
        for (const [text, kind] of [
            [assignment.side.toUpperCase(), `am-chip-side-${assignment.side}`],
            [`$${assignment.dollarAmount}`, "am-chip-amount"],
            [`${assignment.manualPrice}¢`, "am-chip-price"],
        ]) {
            const chip = document.createElement("span");
            chip.className = `am-chip ${kind}`;
            chip.textContent = text;
            meta.appendChild(chip);
        }
        // Assignment line: which order button(s) this market fires with.
        const assign = document.createElement("div");
        assign.className = "am-assign";
        const arrow = document.createElement("span");
        arrow.className = "am-assign-arrow";
        arrow.textContent = "→";
        assign.appendChild(arrow);
        if (assignment.buttons.length === 0) {
            // Parked market: staged in the active set but not yet wired to any
            // button. Show an explicit "Unassigned" pill so the row reads as
            // intentional (and stays click-to-edit) instead of a bare arrow.
            const pill = document.createElement("span");
            pill.className = "am-assign-pill am-assign-unassigned";
            pill.textContent = "Unassigned";
            assign.appendChild(pill);
        }
        for (const b of assignment.buttons) {
            const pill = document.createElement("span");
            // Colour the pill like the physical button it names — NOT like
            // the assignment's own market side. The two differ constantly (a
            // NO-side extra can be wired to the 2PTR YES button), and the
            // operator is looking for which button lights up.
            const tone = (0, order_button_registry_1.getOrderButtonTone)(b);
            pill.className = tone
                ? `am-assign-pill am-assign-pill-${tone}`
                : "am-assign-pill";
            pill.textContent = order_button_registry_1.ORDER_BUTTON_LABELS[b];
            assign.appendChild(pill);
        }
        info.append(ticker, meta, assign);
        // Make the info block a clickable edit trigger
        if (editAssignmentCallback) {
            info.classList.add("am-tooltip-info-editable");
            const cb = editAssignmentCallback;
            info.addEventListener("click", (e) => {
                e.stopPropagation();
                cb(assignment.id);
            });
        }
        const removeBtn = document.createElement("button");
        removeBtn.className = "am-tooltip-remove-btn";
        removeBtn.textContent = "✕";
        removeBtn.title = "Remove this assignment";
        removeBtn.addEventListener("click", (e) => {
            e.stopPropagation();
            (0, additional_markets_service_1.removeAssignment)(assignment.id);
            updateAdditionalMarketBadges();
            updateSettingsInfoAdditionalMarkets();
        });
        row.appendChild(info);
        row.appendChild(removeBtn);
        section.appendChild(row);
    }
    popup.appendChild(section);
}
